"use client";

import { useState, useSyncExternalStore } from "react";
import { Check, Copy, ExternalLink, Share2 } from "lucide-react";
import { Button } from "@v1/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@v1/ui/dropdown-menu";

interface ShareButtonProps {
  title: string;
  url: string;
}

const subscribeToShareAvailability = () => () => {};
const getShareAvailability = () => typeof navigator !== "undefined" && Boolean(navigator.share);
const getServerShareAvailability = () => false;

export function ShareButton({ title, url }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const canNativeShare = useSyncExternalStore(
    subscribeToShareAvailability,
    getShareAvailability,
    getServerShareAvailability,
  );

  const handleCopyLink = async () => {
    setFeedback(null); setFailed(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setFeedback("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      setCopied(false); setFailed(true);
      setFeedback("Couldn’t copy the link. Select and copy the URL below.");
    }
  };

  const handleNativeShare = async () => {
    setFeedback(null); setFailed(false);
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          url,
        });
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to share:", error);
          setFailed(true);
          setFeedback("Couldn’t share this page. Copy the link instead.");
        }
      }
    }
  };

  const outcome = feedback ? <div className="mt-2 max-w-72 space-y-2 text-sm">
    <p role={failed ? "alert" : "status"}>{feedback}</p>
    {failed ? <><input aria-label="Page URL to copy" className="w-full rounded border bg-transparent p-2" readOnly value={url} onFocus={event => event.currentTarget.select()} />
      <Button variant="outline" size="sm" onClick={handleCopyLink}>Copy link</Button></> : null}
  </div> : null;

  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, "_blank", "noopener,noreferrer");
  };

  const shareToLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedInUrl, "_blank", "noopener,noreferrer");
  };

  // Use native share on mobile if available
  if (canNativeShare) {
    return (
      <div><Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 rounded-full text-muted-foreground hover:text-foreground"
        onClick={handleNativeShare}
      >
        <Share2 className="h-4 w-4" />
        <span className="sr-only">Share</span>
      </Button>{outcome}</div>
    );
  }

  return (
    <div><DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-full text-muted-foreground hover:text-foreground"
          />
        }
      >
        <Share2 className="h-4 w-4" />
        <span className="sr-only">Share</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
          {copied ? (
            <Check className="h-4 w-4 mr-2 text-green-500" />
          ) : (
            <Copy className="h-4 w-4 mr-2" />
          )}
          {copied ? "Copied!" : "Copy link"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToTwitter} className="cursor-pointer">
          <ExternalLink className="h-4 w-4 mr-2" />
          Share on X
        </DropdownMenuItem>
        <DropdownMenuItem onClick={shareToLinkedIn} className="cursor-pointer">
          <ExternalLink className="h-4 w-4 mr-2" />
          Share on LinkedIn
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>{outcome}</div>
  );
}
